import { Fragment, lazy, Suspense, useEffect } from "react";
import { createRootRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { TooltipProvider } from "@/client/components/ui/tooltip";
import { Toaster } from "@/client/components/ui/sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { SignInForm } from "@/client/components/auth/sign-in-form";
import { SessionProvider, useSession } from "@/client/hooks/use-session";
import { useTheme } from "@/client/hooks/use-theme";
import "../index.css";

// Development only. Bun inlines NODE_ENV in the bundle, so production drops
// this branch and never ships the devtools.
const RouterDevtools =
  process.env.NODE_ENV === "development"
    ? lazy(() =>
        import("@tanstack/react-router-devtools").then((module) => ({ default: module.TanStackRouterDevtools }))
      )
    : null;

export const Route = createRootRoute({
  component: RootLayout,
});

// Pages that work without an account
const PUBLIC_ROUTES = new Set(["/signin", "/setup", "/agents/authorize"]);

function RootLayout() {
  const { effectiveTheme } = useTheme();
  return (
    <SessionProvider>
      <TooltipProvider delayDuration={300}>
        <div data-region="app" className="h-dvh overflow-hidden bg-background text-foreground">
          <SessionGate />
        </div>
        <Toaster theme={effectiveTheme} richColors closeButton />
        {/* On a phone it would cover the bottom bar */}
        {RouterDevtools && (
          <div className="max-md:hidden">
            <Suspense fallback={null}>
              <RouterDevtools position="bottom-right" />
            </Suspense>
          </div>
        )}
      </TooltipProvider>
    </SessionProvider>
  );
}

function SessionGate() {
  const { status, user } = useSession();
  // Decide by the matched route, not the URL: during a navigation the URL
  // changes before the matches do, and the old route must not render
  const routeId = useRouterState({ select: (state) => state.matches.at(-1)?.routeId });
  const href = useRouterState({ select: (state) => state.location.href });
  const navigate = useNavigate();
  const isPublic = !!routeId && PUBLIC_ROUTES.has(routeId);

  // Signing in comes back to the page that was asked for, like a bookmarked story
  useEffect(() => {
    if (routeId && status === "signed-out" && !isPublic) {
      void navigate({ to: "/signin", search: href === "/" ? {} : { redirect: href } });
    }
    // Only when the session or the kind of page changes, not on every URL change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, routeId, isPublic, navigate]);

  if (isPublic) return <Outlet />;

  if (status === "loading" || status === "signed-out" || !user) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    // Keyed by account, so switching accounts remounts everything
    <Fragment key={user.id}>
      <Outlet />
      <Dialog open={status === "expired"}>
        <DialogContent showCloseButton={false} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Your session expired</DialogTitle>
            <DialogDescription>
              Sign in again to keep saving. Your unsaved writing is kept on this device.
            </DialogDescription>
          </DialogHeader>
          <SignInForm email={user.email} />
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}
